/**
 * src/pages/EditProfile.js
 */

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import ProfilePageShell from "../components/view-profile/ProfilePageShell";
import ProfileEditForm from "../components/profile/ProfileEditForm";
import { useProfile } from "../hooks/useProfile";
import {
  PROFILE_TIMEOUTS,
} from "../constants/profileConstants";

export default function EditProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Use profile and common namespaces with proper fallback chain
  const { t, ready: i18nReady } = useTranslation(["profile", "common"]);
  
  const { profile: hookProfile, updateProfile, loading: hookLoading, signedIn, currentUser } = useProfile(user?.uid || "");

  // Safe translation with fallback - prevents broken UI from missing keys
  const safeT = useMemo(() => {
    return (key, fallback = key) => {
      if (!i18nReady) return fallback;
      const result = t(key);
      // If key not found, i18next returns the key itself
      return result === key ? fallback : result;
    };
  }, [t, i18nReady]);

  const handleCancel = () => {
    navigate(`/profile/${user?.uid}`);
  };

  const handleSubmitSuccess = (uid) => {
    setTimeout(() => {
      navigate(`/profile/${uid}`);
    }, PROFILE_TIMEOUTS.SUCCESS_MESSAGE_DURATION);
  };

  if (hookLoading?.profile) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center p-4">
        <LoadingSpinner 
          size="large" 
          text={safeT("profile.edit.loadingProfile", "Loading profile...")} 
        />
      </div>
    );
  }

  return (
    <ProfilePageShell
      title={safeT("profile.edit.title", "Edit Profile")}
      subtitle={safeT("profile.edit.subtitle", "Update your personal information and preferences")}
      backTo={`/profile/${user?.uid}`}
      backLabel={safeT("profile.edit.backButton", "Back to Profile")}
      maxWidth="max-w-2xl"
    >

        <ProfileEditForm
          profile={hookProfile}
          updateProfile={updateProfile}
          signedIn={signedIn}
          currentUser={currentUser}
          onSubmitSuccess={handleSubmitSuccess}
          onCancel={handleCancel}
          showCancel={false}
          compact={false}
          saveLabel={safeT("profile.edit.buttons.save", "Save")}
          savingLabel={safeT("profile.edit.buttons.saving", "Saving...")}
        />
    </ProfilePageShell>
  );
}